import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TooltipIcon } from "@/components/icons";
import Popover from "@/components/ui/popover";
import {
  PERMISSION_DEPENDENCIES,
  getDependencyReason,
  getReverseDependencies,
  resolvePermissionWithDependencies,
} from "../../config/permission-dependencies";
import styles from "./PermissionDependencyBadge.module.scss";

interface PermissionDependencyBadgeProps {
  permissionKey: string;
  /** Opsiyonel: key → insan-okunur etiket haritası (izin satırında gösterilen ad). */
  getLabel?: (key: string) => string;
}

/**
 * Yetki satırının yanında görünen info rozeti. Hover/click'te bir kart açar:
 *   1. Doğrudan tetiklediği yetkiler (+ neden)
 *   2. Toplam transitive yayılım sayısı
 *   3. Bu yetki hangi yetkiler tarafından isteniyor (reverse)
 *
 * Bağımlılık yoksa rozet render edilmez (rahatsız etmemek için).
 */
const PermissionDependencyBadge: React.FC<PermissionDependencyBadgeProps> = ({
  permissionKey,
  getLabel,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const directs = useMemo(
    () => PERMISSION_DEPENDENCIES[permissionKey] ?? [],
    [permissionKey],
  );
  const allTransitive = useMemo(
    () => resolvePermissionWithDependencies(permissionKey),
    [permissionKey],
  );
  // allTransitive kendisini de içerir → kendisi hariç sayı
  const transitiveCount = Math.max(0, allTransitive.length - 1);

  const reverses = useMemo(
    () => getReverseDependencies(permissionKey),
    [permissionKey],
  );

  if (directs.length === 0 && reverses.length === 0) return null;

  const renderKey = (k: string) => getLabel?.(k) ?? k;

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen((v) => !v);
  };

  const content = (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <span className={styles.cardKey}>{permissionKey}</span>
        <span className={styles.cardMeta}>
          {t(
            "rolesAndPermissions.deps.triggersCount",
            "Tetiklediği: {{count}}",
            { count: transitiveCount },
          )}
        </span>
      </div>

      {directs.length > 0 && (
        <section className={styles.section}>
          <h4 className={styles.sectionTitle}>
            {t("rolesAndPermissions.deps.direct")}
          </h4>
          <ul className={styles.list}>
            {directs.map((dep) => (
              <li key={dep} className={styles.listItem}>
                <div className={styles.listItemHead}>
                  <span className={styles.depLabel}>{renderKey(dep)}</span>
                  <span className={styles.depKey}>{dep}</span>
                </div>
                <p className={styles.reason}>
                  {getDependencyReason(permissionKey, dep)}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {transitiveCount > directs.length && (
        <section className={styles.sectionMuted}>
          <span>
            {t(
              "rolesAndPermissions.deps.transitiveNote",
              "+ zincirleme {{count}} ek yetki daha açılır.",
              { count: transitiveCount - directs.length },
            )}
          </span>
        </section>
      )}

      {reverses.length > 0 && (
        <section className={styles.section}>
          <h4 className={styles.sectionTitle}>
            {t("rolesAndPermissions.deps.reverse")}
          </h4>
          <ul className={styles.reverseList}>
            {reverses.map((rev) => (
              <li key={rev} className={styles.reverseItem}>
                <span className={styles.depLabel}>{renderKey(rev)}</span>
                <span className={styles.depKey}>{rev}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );

  return (
    <Popover
      isOpen={open}
      onClose={() => setOpen(false)}
      content={content}
      position="top"
    >
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        onClick={handleToggle}
        aria-label={t("rolesAndPermissions.deps.showInfo")}
        aria-expanded={open}
      >
        <TooltipIcon width={14} height={14} />
        {directs.length > 0 && (
          <span className={styles.count}>{directs.length}</span>
        )}
      </button>
    </Popover>
  );
};

export default PermissionDependencyBadge;
